import { useMemo, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { ChapbookDivider } from '../shared/components/ChapbookDivider.js';
import { RolePlate } from '../shared/components/RolePlate.js';
import { SecretWordLabel } from '../shared/components/SecretWordLabel.js';
import { Spread } from '../shared/components/Spread.js';
import { Timer } from '../shared/components/Timer.js';
import { CastGrid } from './components/CastGrid.js';
import type { DiscussionPlayer, DiscussionView } from './types.js';
import './DiscussionPhase.css';

interface DiscussionPhaseProps {
  view: DiscussionView;
  /**
   * Invoked when the local viewer taps a cast cell — `targetId` is a player id
   * or `ANOTHER_ROUND`; re-tapping the current target clears the vote. In
   * production this dispatches `changeVote`; in mock-state mode the host stores
   * it into the local view's votes map so the cluster + "your vote" update.
   */
  onVote?: (targetId: string) => void;
  /** Cold-arrival flag: suppresses the cast/cluster/clue entry beats on the
   *  first render after reconnect / boot-into-discussion. Default `false`. */
  justArrived?: boolean;
}

/**
 * The Discussion destination. Verso (left) carries the action — timer, prompt,
 * and the interactive cast grid (every alive player plus the Another-Round
 * sentinel). Recto (right) carries the viewer's own identity for the round
 * (role plate + the secret as a sentence), the same "your hand" panel as the
 * meeting and clue-giving rectos.
 *
 * POV palette persists from clue-giving: Villager / Fool warm, Ghost cooled.
 * Oxblood is held back entirely until vote-lock (the resolution beat) — the
 * live voting surface is accent-free.
 */
export function DiscussionPhase({ view, onVote, justArrived = false }: DiscussionPhaseProps): JSX.Element {
  const { t } = useTranslation();

  const votersByTarget = useMemo(() => {
    const byId = new Map(view.players.map((p) => [p.id, p]));
    const map = new Map<string, DiscussionPlayer[]>();
    // MapSchema iteration order = vote-arrival recency; the cluster relies on it.
    for (const [voterId, targetId] of view.votes) {
      const voter = byId.get(voterId);
      if (voter === undefined) continue;
      const list = map.get(targetId);
      if (list) list.push(voter);
      else map.set(targetId, [voter]);
    }
    return map;
  }, [view.votes, view.players]);

  const myVoteTarget = view.votes.get(view.meId);
  const spreadPov: 'village' | 'ghost' = view.pov === 'ghost' ? 'ghost' : 'village';
  const isSplit = view.resolution.kind === 'split';
  /* A dead viewer (eliminated earlier, still watching) gets the static
     spectating ballot — the server rejects dead votes, so the grid must not
     present as castable. The voting instruction is withheld for the same
     reason; the live tally (clusters) is public and stays. */
  const spectating = !view.meAlive;

  return (
    <div
      className="susdle-discussion"
      data-vote-locked={view.resolution.kind === 'eliminate' ? 'yes' : 'no'}
    >
      <h1 className="susdle-sr-only">{t('discussion.ariaLabel')}</h1>
      <Spread
        pov={spreadPov}
        ariaLabel={t('discussion.ariaLabel')}
        verso={
          <div className="susdle-discussion-verso">
            {isSplit ? (
              <p className="susdle-discussion-verso__split">{t('discussion.split')}</p>
            ) : (
              <>
                <div className="susdle-discussion-verso__timer">
                  <Timer phaseEndsAt={view.phaseEndsAt} durationMs={view.durationMs} />
                </div>
                {spectating ? null : (
                  <p className="susdle-discussion-verso__prompt">{t('discussion.prompt')}</p>
                )}
              </>
            )}
            <CastGrid
              players={view.players}
              votersByTarget={votersByTarget}
              resolution={view.resolution}
              justArrived={justArrived}
              spectating={spectating}
              {...(!spectating && myVoteTarget !== undefined ? { myVoteTarget } : {})}
              {...(!spectating && onVote !== undefined ? { onVote } : {})}
            />
          </div>
        }
        recto={
          <div className="susdle-discussion-recto">
            <RolePlate
              preamble={t('discussion.rolePlate.preamble')}
              roleName={t(view.pov === 'ghost' ? 'meeting.rolePlate.ghost' : 'meeting.rolePlate.villager')}
            />
            <ChapbookDivider />
            <div className="susdle-discussion-recto__secret">
              {view.secret.mode === 'word' ? (
                <SecretWordLabel mode="word" word={view.secret.value} />
              ) : (
                <SecretWordLabel mode="length" length={view.secret.value} />
              )}
            </div>
          </div>
        }
      />
    </div>
  );
}
